import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { ModeIcon } from '@/components/mode/ModeIcon';
import { ModeWordmark } from '@/components/mode/ModeWordmark';
import { track } from '@/lib/analytics';
import { MODE_DESCRIPTIONS, MODE_LABELS, MODE_OPTIONS } from '@/lib/modes';
import { useLearnerPreferences, type Dialect } from '@/providers/LearnerPreferences';

export function ModeOptionButton({
  option,
  selected,
  onSelect,
}: {
  option: Dialect;
  selected: boolean;
  onSelect: (value: Dialect) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`${MODE_LABELS[option]}, ${MODE_DESCRIPTIONS[option]}`}
      onClick={() => onSelect(option)}
      className={`flex w-full items-center gap-3.5 rounded-[14px] px-3 py-3 text-left hover:bg-[rgba(57,153,24,0.08)] ${
        selected ? 'bg-[rgba(57,153,24,0.08)]' : ''
      }`}
    >
      <ModeIcon mode={option} size={44} />
      <span className="min-w-0 flex-1">
        <ModeWordmark dialect={option} variant="option" />
        <span className="mt-px block font-nunito text-[13px] font-normal text-[#6B7B69]">
          {MODE_DESCRIPTIONS[option]}
        </span>
      </span>
    </button>
  );
}

export function ModeSwitchTrigger({
  source,
  anchorRef,
}: {
  source: string;
  anchorRef?: RefObject<HTMLElement | null>;
}) {
  const { dialect } = useLearnerPreferences();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [contentTop, setContentTop] = useState(72);

  const measure = () => {
    const el = anchorRef?.current ?? buttonRef.current;
    if (!el) return 72;
    return el.getBoundingClientRect().bottom;
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          track('Switch Mode Press', { source, mode: dialect });
          setOpen(true);
          setContentTop(measure());
        }}
        aria-label={`Current mode: ${MODE_LABELS[dialect]}. Tap to switch mode.`}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <ModeIcon mode={dialect} size={40} bordered />
      </button>
      <ModeSwitchModal
        visible={open}
        contentTop={contentTop}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

export function ModeSwitchModal({
  visible,
  onClose,
  contentTop = 72,
}: {
  visible: boolean;
  onClose: () => void;
  contentTop?: number;
}) {
  const { dialect, setDialect } = useLearnerPreferences();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [visible, onClose]);

  if (!visible) return null;

  const choose = (value: Dialect) => {
    track('Switch Mode', { mode: value, previous_mode: dialect });
    setDialect(value);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[80] flex flex-col" data-testid="mode-switch-modal">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Dismiss mode switcher"
        onClick={onClose}
      />
      <div className="pointer-events-none shrink-0" style={{ height: contentTop }} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative animate-[fadeIn_220ms_ease-out] rounded-b-[20px] bg-[#D8EBD2] px-2 py-2 shadow-[0_6px_12px_rgba(0,0,0,0.12)] outline-none"
      >
        <h2 id={titleId} className="sr-only">
          Switch dialect
        </h2>
        {MODE_OPTIONS.map((option) => (
          <ModeOptionButton
            key={option}
            option={option}
            selected={option === dialect}
            onSelect={choose}
          />
        ))}
      </div>
      <div className="pointer-events-none min-h-0 flex-1 bg-[rgba(0,0,0,0.62)]" />
    </div>
  );
}
